// Modal "Ubicación de retiro" (Carrusel / OfertaDelDia en el Home): lógica
// pura de normalización de datos + cálculo de encuadre del mapa. Módulo puro
// (sin React/Zustand/Sentry), mismo patrón y misma prohibición de mockeo en
// los tests de componente que src/lib/mpCheckout.js / oneclickCards.js /
// paymentGuard.js / transbankCredenciales.js.
//
// Por qué existe: (a) `/apiV2/maquina/listaMaquinas` puede responder HTTP 200
// con `{status:"error", message:...}` para parámetros inválidos — sin una
// guarda explícita, ese objeto se contaba como "lista no vacía"
// (`_.size(objeto) > 0`) y un `.map` sobre él tumbaba toda la página
// (ErrorBoundary "routes"); y (b) el centro del mapa se elegía como el índice
// 0 de la lista de máquinas de *toda la comuna* (todas las empresas), no de
// la empresa del banner/oferta — con datos reales de QA eso deja el pin de la
// empresa fuera de pantalla o el mapa centrado en una coordenada corrupta de
// un tercero (ver CLAUDE.md § modal de ubicaciones).
//
// pigeon-maps@0.21.6 no expone `fitBounds` ni una prop `bounds`
// (node_modules/pigeon-maps/src/types.ts) — el encuadre por bounds se calcula
// en la app.

// Límites de zoom del mapa del modal (viewport fijo de 480px de alto). No
// acercar más allá de MAPA_ZOOM_PUNTO_UNICO cuando hay un solo punto, ni
// alejar más allá de MAPA_MIN_ZOOM para una comuna dispersa.
export const MAPA_MIN_ZOOM = 9;
export const MAPA_MAX_ZOOM = 16;
export const MAPA_ZOOM_PUNTO_UNICO = 16;

// Margen restado al zoom calculado por span, para dejar aire alrededor de
// los pines extremos (que no queden pegados al borde del viewport).
export const MAPA_PADDING_ZOOM = 0.6;

// Radio máximo (km) respecto de la mediana de las máquinas de la empresa en
// la comuna, para descartar una coordenada corrupta de la propia empresa sin
// vaciar el conjunto si todas están genuinamente dispersas. 60km cubre una
// conurbación grande (p. ej. Gran Santiago); una comuna real nunca lo excede.
export const MAPA_DISTANCIA_MAX_KM = 60;

// Centro por defecto del mapa (el mismo valor hardcodeado que ya usaba el
// componente original), usado antes de tener datos o cuando la comuna
// seleccionada no tiene máquinas de la empresa.
export const MAPA_CENTRO_DEFECTO = [-33.38661163954975, -70.68118225513098];

// Copys provisorios (§8 preguntas abiertas del requerimiento: copy exacto
// pendiente de confirmación con producto). Centralizados aquí para cambiarlos
// en un solo lugar.
export const MAPA_MSG_SIN_MAQUINAS =
  "No hay máquinas de esta empresa en la comuna seleccionada.";
export const MAPA_MSG_ERROR_CARGA =
  "No pudimos cargar las ubicaciones. Intenta nuevamente.";

// Rango aproximado de Chile continental + insular cercano, usado como
// primera barrera contra coordenadas evidentemente corruptas (p. ej. la
// máquina 63 de QA, etiquetada Providencia con lat/lon de la Región de
// Coquimbo).
const CHILE_LAT_MIN = -56;
const CHILE_LAT_MAX = -17;
const CHILE_LON_MIN = -76;
const CHILE_LON_MAX = -66;

const NUMERIC_STRING_RE = /^-?\d+(\.\d+)?$/;

// Mismo criterio estricto que parseVerifiableNumber de mpCheckout.js:
// Number(null) === 0, Number("") === 0 — hay que rechazar explícitamente
// cualquier valor que no sea number finito o string que matchee el patrón
// numérico, para no terminar centrando el mapa en 0,0.
const parseNumeroVerificable = (valor) => {
  if (typeof valor === "number") {
    return Number.isFinite(valor) ? valor : null;
  }
  if (typeof valor === "string") {
    const recortado = valor.trim();
    if (!NUMERIC_STRING_RE.test(recortado)) return null;
    const parsed = Number(recortado);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/**
 * normalizarListaMaquinas(payload)
 * -> { maquinas: Array, estado: "ok" | "vacio" | "invalido" }
 * Nunca lanza. `Array.isArray(payload)` decide "ok"/"vacio"; cualquier otra
 * forma (objeto de error `{status:"error",...}`, null, string, number,
 * objeto vacío) se trata como "invalido" con `maquinas: []`.
 */
export const normalizarListaMaquinas = (payload) => {
  if (Array.isArray(payload)) {
    return { maquinas: payload, estado: payload.length > 0 ? "ok" : "vacio" };
  }
  return { maquinas: [], estado: "invalido" };
};

/**
 * coordenadaValida(lat, lon)
 * Rechaza null/""/no numérico y acota a un rango plausible de Chile, para
 * que una coordenada corrupta ni siquiera entre al cálculo de encuadre.
 */
export const coordenadaValida = (lat, lon) => {
  const latNum = parseNumeroVerificable(lat);
  const lonNum = parseNumeroVerificable(lon);
  if (latNum === null || lonNum === null) return false;

  if (latNum < CHILE_LAT_MIN || latNum > CHILE_LAT_MAX) return false;
  if (lonNum < CHILE_LON_MIN || lonNum > CHILE_LON_MAX) return false;

  return true;
};

/**
 * filtrarMaquinasPorEmpresa(maquinas, empresaId)
 * `empresaId` ''/null/0/undefined -> [] (criterio 15, cubre el caso
 * empresaId inicial '' de useEmpresaLocations). Comparación laxa por String
 * (el backend mezcla number/string). También descarta coordenadas inválidas.
 */
export const filtrarMaquinasPorEmpresa = (maquinas, empresaId) => {
  if (!Array.isArray(maquinas)) return [];
  if (empresaId === "" || empresaId === null || empresaId === undefined || empresaId === 0) {
    return [];
  }

  const empresaIdStr = String(empresaId);
  return maquinas.filter((item) => {
    if (!item) return false;
    if (String(item.empresa_id) !== empresaIdStr) return false;
    return coordenadaValida(item.maquina_latitud, item.maquina_longitud);
  });
};

const mediana = (valores) => {
  const ordenados = [...valores].sort((a, b) => a - b);
  const mitad = Math.floor(ordenados.length / 2);
  return ordenados.length % 2 !== 0
    ? ordenados[mitad]
    : (ordenados[mitad - 1] + ordenados[mitad]) / 2;
};

const HAVERSINE_R_KM = 6371;
const distanciaKm = (lat1, lon1, lat2, lon2) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return HAVERSINE_R_KM * c;
};

/**
 * descartarOutliers(maquinas)
 * Descarta puntos a más de MAPA_DISTANCIA_MAX_KM de la mediana (robusta),
 * salvo que eso vaciara el conjunto (2 puntos genuinamente lejanos entre sí:
 * no se descarta ninguno, se deja que el zoom se aleje).
 */
export const descartarOutliers = (maquinas) => {
  if (!Array.isArray(maquinas) || maquinas.length <= 1) return maquinas ?? [];

  const lat = mediana(maquinas.map((m) => Number(m.maquina_latitud)));
  const lon = mediana(maquinas.map((m) => Number(m.maquina_longitud)));

  const dentro = maquinas.filter(
    (m) => distanciaKm(lat, lon, Number(m.maquina_latitud), Number(m.maquina_longitud)) <= MAPA_DISTANCIA_MAX_KM,
  );

  return dentro.length > 0 ? dentro : maquinas;
};

// Matemática Web Mercator (tiles de 256px, la que usa pigeon-maps) para
// derivar el zoom que hace caber un span de lat/lon dado en un viewport de
// widthPx x heightPx.
const TILE_SIZE = 256;
const latToMercatorY = (lat) => {
  const rad = (lat * Math.PI) / 180;
  return Math.log(Math.tan(Math.PI / 4 + rad / 2));
};

/**
 * calcularEncuadre(maquinas, { widthPx, heightPx })
 * -> { center: [lat, lon], zoom } | null si no hay máquinas válidas.
 */
export const calcularEncuadre = (maquinas, { widthPx, heightPx = 480 } = {}) => {
  if (!Array.isArray(maquinas) || maquinas.length === 0) return null;

  const puntos = maquinas
    .map((m) => [Number(m.maquina_latitud), Number(m.maquina_longitud)])
    .filter(([lat, lon]) => coordenadaValida(lat, lon));

  if (puntos.length === 0) return null;

  const lats = puntos.map((p) => p[0]);
  const lons = puntos.map((p) => p[1]);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLon = Math.min(...lons);
  const maxLon = Math.max(...lons);
  const center = [(minLat + maxLat) / 2, (minLon + maxLon) / 2];

  const spanLon = maxLon - minLon;
  const spanLatMercator = Math.abs(latToMercatorY(maxLat) - latToMercatorY(minLat));

  // Un solo punto (o span ~0): zoom fijo de "acercado".
  if (spanLon < 1e-9 && spanLatMercator < 1e-9) {
    return { center, zoom: MAPA_ZOOM_PUNTO_UNICO };
  }

  const width = widthPx && widthPx > 0 ? widthPx : 700;
  const height = heightPx && heightPx > 0 ? heightPx : 480;

  const zoomLon = Math.log2((width / TILE_SIZE) * (360 / Math.max(spanLon, 1e-9)));
  const zoomLat = Math.log2((height / TILE_SIZE) * ((2 * Math.PI) / Math.max(spanLatMercator, 1e-9)));

  const zoomCrudo = Math.floor(Math.min(zoomLon, zoomLat) - MAPA_PADDING_ZOOM);
  const zoom = Math.min(MAPA_MAX_ZOOM, Math.max(MAPA_MIN_ZOOM, zoomCrudo));

  return { center, zoom };
};
